import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    useProject,
    useProjectSubscriptions,
    useProjectPayments,
    useUpdateProjectSubscription,
    useCreateProjectPayment,
} from "../../api/projects";
import { ProjectSubscription } from "../../types/projects";

type SubscriptionStatusFilter = "all" | "active" | "paused";

const ProjectSubscribers: React.FC = () => {
    const params = useParams();
    const navigate = useNavigate();
    const projectId = params.projectId ? Number(params.projectId) : null;

    const { data: projectResponse } = useProject(projectId);
    const project = projectResponse?.data;

    const [statusFilter, setStatusFilter] =
        useState<SubscriptionStatusFilter>("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [subscriptionPage, setSubscriptionPage] = useState(1);

    const [paymentPage, setPaymentPage] = useState(1);
    const [paymentFilters, setPaymentFilters] = useState({
        date_from: "",
        date_to: "",
    });
    const [selectedSubscriptionForPayment, setSelectedSubscriptionForPayment] =
        useState<number | null>(null);
    const [paymentForm, setPaymentForm] = useState({
        amount: "",
        currency: "USD",
        coefficient: "",
        charged_at: "",
    });
    const [paymentError, setPaymentError] = useState<string | null>(null);

    const isActiveParam = useMemo(() => {
        if (statusFilter === "active") {
            return true;
        }
        if (statusFilter === "paused") {
            return false;
        }
        return undefined;
    }, [statusFilter]);

    const {
        data: subscriptionsData,
        isLoading: subscriptionsLoading,
        refetch: refetchSubscriptions,
    } = useProjectSubscriptions(projectId, {
        page: subscriptionPage,
        per_page: 10,
        is_active: isActiveParam,
        search: searchTerm || undefined,
    });

    const {
        data: paymentsData,
        isLoading: paymentsLoading,
        refetch: refetchPayments,
    } = useProjectPayments(projectId, {
        page: paymentPage,
        per_page: 10,
        date_from: paymentFilters.date_from || undefined,
        date_to: paymentFilters.date_to || undefined,
    });

    const updateSubscriptionMutation = useUpdateProjectSubscription();
    const createPaymentMutation = useCreateProjectPayment();

    const subscriptions = subscriptionsData?.data ?? [];
    const subscriptionMeta = subscriptionsData?.meta;
    const payments = paymentsData?.data ?? [];
    const paymentsMeta = paymentsData?.meta;
    const paymentsSummary = paymentsData?.summary ?? {};

    const applySubscriptionFilters = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubscriptionPage(1);
        refetchSubscriptions();
    };

    const applyPaymentFilters = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setPaymentPage(1);
        refetchPayments();
    };

    const toggleSubscriptionStatus = async (subscription: ProjectSubscription) => {
        if (!projectId) {
            return;
        }
        try {
            await updateSubscriptionMutation.mutateAsync({
                projectId,
                subscriptionId: subscription.id,
                payload: { is_active: !subscription.is_active },
            });
            refetchSubscriptions();
        } catch (err: any) {
            window.alert(
                err?.response?.data?.message ||
                    err?.message ||
                    "Не удалось обновить статус подписки."
            );
        }
    };

    const editSubscription = async (subscription: ProjectSubscription) => {
        if (!projectId) {
            return;
        }
        const newCoefficient = window.prompt(
            "Введите коэффициент (1-10):",
            String(subscription.coefficient)
        );
        if (!newCoefficient) {
            return;
        }
        const coefficientValue = Number(newCoefficient);
        if (Number.isNaN(coefficientValue) || coefficientValue < 1 || coefficientValue > 10) {
            window.alert("Коэффициент должен быть числом от 1 до 10.");
            return;
        }

        const newBillingDay = window.prompt(
            "Введите день списания (1-28):",
            String(subscription.billing_day)
        );
        if (!newBillingDay) {
            return;
        }
        const billingDayValue = Number(newBillingDay);
        if (Number.isNaN(billingDayValue) || billingDayValue < 1 || billingDayValue > 28) {
            window.alert("День списания должен быть числом от 1 до 28.");
            return;
        }

        try {
            await updateSubscriptionMutation.mutateAsync({
                projectId,
                subscriptionId: subscription.id,
                payload: {
                    coefficient: coefficientValue,
                    billing_day: billingDayValue,
                },
            });
            refetchSubscriptions();
        } catch (err: any) {
            window.alert(
                err?.response?.data?.message ||
                    err?.message ||
                    "Не удалось обновить параметры подписки."
            );
        }
    };

    const openPaymentForm = (subscriptionId: number, coefficient: number) => {
        setSelectedSubscriptionForPayment(subscriptionId);
        setPaymentForm({
            amount: "",
            currency: "USD",
            coefficient: String(coefficient),
            charged_at: "",
        });
        setPaymentError(null);
    };

    const submitPayment = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!projectId || !selectedSubscriptionForPayment) {
            setPaymentError("Выберите подписку для платежа.");
            return;
        }
        if (!paymentForm.amount) {
            setPaymentError("Укажите сумму платежа.");
            return;
        }
        try {
            await createPaymentMutation.mutateAsync({
                projectId,
                subscriptionId: selectedSubscriptionForPayment,
                amount: paymentForm.amount,
                currency: paymentForm.currency as "USD" | "USDT",
                coefficient: paymentForm.coefficient
                    ? Number(paymentForm.coefficient)
                    : undefined,
                charged_at: paymentForm.charged_at || undefined,
            });
            setSelectedSubscriptionForPayment(null);
            setPaymentError(null);
            refetchPayments();
            refetchSubscriptions();
        } catch (err: any) {
            setPaymentError(
                err?.response?.data?.message ||
                    err?.message ||
                    "Не удалось добавить платёж."
            );
        }
    };

    if (!projectId) {
        return (
            <div className="p-6 text-red-500">Некорректный идентификатор проекта.</div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            <div>
                <button
                    onClick={() => navigate(-1)}
                    className="mb-3 inline-flex items-center text-sm text-blue-600 hover:text-blue-700 transition"
                >
                    ← Назад
                </button>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    Подписчики проекта {project?.name}
                </h1>
                <p className="text-gray-600 dark:text-gray-300 mt-1">
                    Управляйте активными пользователями, контролируйте настройки подписки и добавляйте платежи вручную.
                </p>
            </div>

            <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-6 space-y-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Фильтр подписок
                </h2>
                <form
                    onSubmit={applySubscriptionFilters}
                    className="grid grid-cols-1 md:grid-cols-4 gap-4"
                >
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Статус
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) =>
                                setStatusFilter(e.target.value as SubscriptionStatusFilter)
                            }
                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">Все подписки</option>
                            <option value="active">Активные</option>
                            <option value="paused">Остановленные</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Поиск (имя, email, ID)
                        </label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Например, Ivan или user@example.com"
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                        >
                            Применить
                        </button>
                    </div>
                </form>
            </div>

            <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Список подписчиков
                </h2>
                {subscriptionsLoading ? (
                    <div className="text-gray-500 dark:text-gray-300">
                        Загрузка подписчиков...
                    </div>
                ) : subscriptions.length ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Пользователь
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Параметры
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Статистика
                                    </th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Действия
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                {subscriptions.map((subscription) => (
                                    <tr key={subscription.id}>
                                        <td className="px-4 py-3">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                {subscription.user?.name ||
                                                    subscription.user?.email ||
                                                    `ID: ${subscription.user_id}`}
                                            </div>
                                            {subscription.user?.email && (
                                                <div className="text-xs text-gray-500">
                                                    {subscription.user.email}
                                                </div>
                                            )}
                                            <div className="text-xs text-gray-500">
                                                ID пользователя: {subscription.user_id}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 space-y-1">
                                            <div>Коэффициент: x{subscription.coefficient}</div>
                                            <div>День списания: {subscription.billing_day}</div>
                                            <div>
                                                Статус:{" "}
                                                {subscription.is_active ? (
                                                    <span className="text-green-600">Активна</span>
                                                ) : (
                                                    <span className="text-gray-500">
                                                        Остановлена
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 space-y-1">
                                            <div>
                                                Сумма взносов: {subscription.total_paid_amount} USD
                                            </div>
                                            <div>
                                                Доля: {subscription.dividend_share_percent}%
                                                {subscription.is_locked && (
                                                    <span className="text-xs text-gray-500 ml-1">
                                                        (зафиксировано)
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                Последнее списание:{" "}
                                                {subscription.last_charged_at
                                                    ? new Date(
                                                          subscription.last_charged_at
                                                      ).toLocaleString()
                                                    : "ещё не списывалось"}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right space-y-2">
                                            <button
                                                onClick={() => editSubscription(subscription)}
                                                className="w-full px-3 py-1 bg-indigo-500 text-white text-sm rounded hover:bg-indigo-600 transition"
                                            >
                                                Изменить
                                            </button>
                                            <button
                                                onClick={() =>
                                                    toggleSubscriptionStatus(subscription)
                                                }
                                                className="w-full px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 transition"
                                            >
                                                {subscription.is_active
                                                    ? "Приостановить"
                                                    : "Возобновить"}
                                            </button>
                                            <button
                                                onClick={() =>
                                                    openPaymentForm(
                                                        subscription.id,
                                                        subscription.coefficient
                                                    )
                                                }
                                                className="w-full px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition"
                                            >
                                                Добавить платеж
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-gray-500 dark:text-gray-300">
                        Подписчики отсутствуют.
                    </div>
                )}

                {subscriptionMeta && subscriptions.length > 0 && (
                    <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-300">
                        <div>
                            Страница {subscriptionMeta.current_page} из{" "}
                            {subscriptionMeta.last_page}. Всего подписок:{" "}
                            {subscriptionMeta.total}.
                        </div>
                        <div className="space-x-2">
                            <button
                                disabled={subscriptionPage <= 1}
                                onClick={() =>
                                    setSubscriptionPage((prev) => Math.max(1, prev - 1))
                                }
                                className={`px-3 py-1 rounded border ${
                                    subscriptionPage <= 1
                                        ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                        : "text-gray-700 border-gray-300 hover:bg-gray-100"
                                }`}
                            >
                                Назад
                            </button>
                            <button
                                disabled={subscriptionPage >= subscriptionMeta.last_page}
                                onClick={() =>
                                    setSubscriptionPage((prev) =>
                                        Math.min(subscriptionMeta.last_page, prev + 1)
                                    )
                                }
                                className={`px-3 py-1 rounded border ${
                                    subscriptionPage >= subscriptionMeta.last_page
                                        ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                        : "text-gray-700 border-gray-300 hover:bg-gray-100"
                                }`}
                            >
                                Далее
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-6 space-y-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Ручное начисление платежа
                </h2>
                {selectedSubscriptionForPayment === null ? (
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                        Для создания ручного платежа выберите подписку и нажмите кнопку
                        «Добавить платёж» в списке подписчиков.
                    </div>
                ) : (
                    <form
                        onSubmit={submitPayment}
                        className="grid grid-cols-1 md:grid-cols-5 gap-4"
                    >
                        <div className="md:col-span-2 text-sm text-gray-700 dark:text-gray-300">
                            Подписка ID: {selectedSubscriptionForPayment}
                        </div>
                        <div>
                            <label className="block text-xs uppercase text-gray-500 mb-1">
                                Сумма
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={paymentForm.amount}
                                onChange={(e) =>
                                    setPaymentForm((prev) => ({
                                        ...prev,
                                        amount: e.target.value,
                                    }))
                                }
                                className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase text-gray-500 mb-1">
                                Валюта
                            </label>
                            <select
                                value={paymentForm.currency}
                                onChange={(e) =>
                                    setPaymentForm((prev) => ({
                                        ...prev,
                                        currency: e.target.value,
                                    }))
                                }
                                className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="USD">USD</option>
                                <option value="USDT">USDT</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs uppercase text-gray-500 mb-1">
                                Коэффициент
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={paymentForm.coefficient}
                                onChange={(e) =>
                                    setPaymentForm((prev) => ({
                                        ...prev,
                                        coefficient: e.target.value,
                                    }))
                                }
                                className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Необязательно"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase text-gray-500 mb-1">
                                Дата платежа
                            </label>
                            <input
                                type="datetime-local"
                                value={paymentForm.charged_at}
                                onChange={(e) =>
                                    setPaymentForm((prev) => ({
                                        ...prev,
                                        charged_at: e.target.value,
                                    }))
                                }
                                className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="По умолчанию текущее время"
                            />
                        </div>
                        <div className="md:col-span-5 flex items-center justify-end space-x-3">
                            <button
                                type="button"
                                onClick={() => setSelectedSubscriptionForPayment(null)}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                            >
                                Отмена
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                            >
                                Добавить платёж
                            </button>
                        </div>
                        {paymentError && (
                            <div className="md:col-span-5 text-sm text-red-500">
                                {paymentError}
                            </div>
                        )}
                    </form>
                )}
            </div>

            <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        История платежей
                    </h2>
                    <div className="text-sm text-gray-500">
                        Сумма по фильтру:{" "}
                        {typeof paymentsSummary.total_amount === "string"
                            ? `${paymentsSummary.total_amount} USD`
                            : "-"}
                    </div>
                </div>

                <form
                    onSubmit={applyPaymentFilters}
                    className="grid grid-cols-1 md:grid-cols-4 gap-4"
                >
                    <div>
                        <label className="block text-xs uppercase text-gray-500 mb-1">
                            От даты
                        </label>
                        <input
                            type="date"
                            value={paymentFilters.date_from}
                            onChange={(e) =>
                                setPaymentFilters((prev) => ({
                                    ...prev,
                                    date_from: e.target.value,
                                }))
                            }
                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs uppercase text-gray-500 mb-1">
                            До даты
                        </label>
                        <input
                            type="date"
                            value={paymentFilters.date_to}
                            onChange={(e) =>
                                setPaymentFilters((prev) => ({
                                    ...prev,
                                    date_to: e.target.value,
                                }))
                            }
                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                        >
                            Применить
                        </button>
                    </div>
                </form>

                {paymentsLoading ? (
                    <div className="text-gray-500 dark:text-gray-300">
                        Загрузка платежей...
                    </div>
                ) : payments.length ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Дата
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Пользователь
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Сумма
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Коэф.
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                {payments.map((payment) => (
                                    <tr key={payment.id}>
                                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                            {new Date(payment.created_at).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                            Subscription #{payment.project_subscription_id} • User{" "}
                                            {payment.user_id}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                            {payment.amount} {payment.currency}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                            x{payment.coefficient}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-gray-500 dark:text-gray-300">
                        Платежей пока нет.
                    </div>
                )}

                {paymentsMeta && payments.length > 0 && (
                    <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-300">
                        <div>
                            Страница {paymentsMeta.current_page} из {paymentsMeta.last_page}.
                        </div>
                        <div className="space-x-2">
                            <button
                                disabled={paymentPage <= 1}
                                onClick={() => setPaymentPage((prev) => Math.max(1, prev - 1))}
                                className={`px-3 py-1 rounded border ${
                                    paymentPage <= 1
                                        ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                        : "text-gray-700 border-gray-300 hover:bg-gray-100"
                                }`}
                            >
                                Назад
                            </button>
                            <button
                                disabled={paymentPage >= paymentsMeta.last_page}
                                onClick={() =>
                                    setPaymentPage((prev) =>
                                        Math.min(paymentsMeta.last_page, prev + 1)
                                    )
                                }
                                className={`px-3 py-1 rounded border ${
                                    paymentPage >= paymentsMeta.last_page
                                        ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                        : "text-gray-700 border-gray-300 hover:bg-gray-100"
                                }`}
                            >
                                Далее
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectSubscribers;

